"use client";

/**
 * TokenBalanceWidget — compact dashboard tile showing current BB-token balance,
 * monthly-subscription status, and a "Buy / Manage" CTA. Designed to drop into
 * /dashboard or /profile without taking much vertical real estate.
 */

import Link from "next/link";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/auth-context";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Coins, ArrowRight, Loader2, Repeat } from "lucide-react";
import { tokensToUsd } from "@/data/tokens";

export function TokenBalanceWidget({ className }: { className?: string }) {
  const { user, loading } = useAuth();
  const [busy, setBusy] = useState(true);
  const [balance, setBalance] = useState<number | null>(null);
  const [sub, setSub] = useState<any>(null);

  const load = useCallback(async () => {
    if (!user) { setBusy(false); return; }
    try {
      const token = await user.getIdToken();
      const res = await fetch("/api/tokens/balance", { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (d.ok) { setBalance(d.balance); setSub(d.subscription || null); }
    } catch { /* ignore */ }
    finally { setBusy(false); }
  }, [user]);

  useEffect(() => { if (!loading) load(); }, [loading, load]);

  if (loading || !user) return null;

  return (
    <Card className={className}>
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-500/15 text-amber-500 flex items-center justify-center flex-shrink-0">
            <Coins className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">BB Tokens</p>
            {busy ? (
              <p className="text-lg font-bold flex items-center gap-1.5"><Loader2 className="h-4 w-4 animate-spin" /></p>
            ) : (
              <>
                <p className="text-xl font-bold leading-tight">{balance === null ? "—" : balance.toLocaleString()}</p>
                {balance !== null && <p className="text-[11px] text-muted-foreground">≈ {tokensToUsd(balance)} · powers AI features</p>}
                {sub?.status === "active" && (
                  <p className="text-[10px] text-emerald-500 font-medium mt-0.5 flex items-center gap-1"><Repeat className="h-3 w-3" /> {sub.monthlyTokens?.toLocaleString()}/mo {sub.planId ? `· ${sub.planId}` : ""}</p>
                )}
              </>
            )}
          </div>
          <Link href="/tokens">
            <Button size="sm" variant="outline" className="h-8 text-xs">
              {(sub?.status === "active") ? "Manage" : "Buy"} <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
